import { useContext, useMemo } from 'react'
import { Like } from '../types/likes'
import { useProfilesByIds } from 'containers/profile/hooks/useProfile'
import { Avatar } from 'containers/recommendations/Avatar'
import { cssClass, userFullName } from '@/shared/utils'
import { Text } from '@/components/commons/typography/Text'
import { BrowserContext } from 'context'
import { getProfileLink } from 'containers/profile/utilities/profile'
import Link from 'next/link'

interface LikesProfilesProps {
  likes: Like[]
  totalLikes: number
  isProjectPage?: boolean
}

export const LikesProfiles = ({
  likes,
  totalLikes,
  isProjectPage,
}: LikesProfilesProps) => {
  const { isMobile } = useContext(BrowserContext)
  const profilesIds = useMemo(() => {
    return likes.map((like) => like.userWhoLikedId).slice(0, 2) || []
  }, [likes])

  const { data: profiles } = useProfilesByIds(profilesIds)

  const firstProfile = useMemo(() => {
    return profiles?.[0]
  }, [profiles])

  const name = useMemo(() => {
    if (!profiles?.length) return ''

    let profileName = userFullName(firstProfile)

    let maxProfileNameLength = totalLikes > 1 ? 10 : 16

    if (!isMobile && isProjectPage) {
      maxProfileNameLength = 25
    }
    // Validate if profile name is too long
    if (profileName.length > maxProfileNameLength) {
      profileName = `${profileName.slice(0, maxProfileNameLength)}...`
    }

    return profileName
  }, [firstProfile, isMobile, isProjectPage, profiles?.length, totalLikes])

  if (likes.length === 0) {
    return null
  }

  return (
    <div className="flex items-center gap-10">
      <div className="flex">
        {profiles?.map((profile, index) => (
          <div
            key={profile.id}
            className={cssClass([
              'relative border-white border-1 rounded-6 border-solid rounded-8',
              index > 0 ? '-ml-12 z-2' : 'z-10',
            ])}
          >
            {profile?.avatar && (
              <Avatar
                size={27}
                image={profile?.avatar}
                to={getProfileLink(profile)}
              />
            )}
          </div>
        ))}
      </div>
      <Text sizeClass="text-12">
        Liked by{' '}
        <Link href={getProfileLink(firstProfile)}>
          <span className="font-bold hover__text-underline">{name}</span>
        </Link>{' '}
        {totalLikes > 1 && `and ${totalLikes - 1} others`}
      </Text>
    </div>
  )
}
